import React, { useState, useEffect, useCallback } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
} from "firebase/firestore";
import { signOut } from "firebase/auth";
import { db, auth } from "./firebase";
import "./AdminPanel.css";

import {
  fetchLeanAnalytics,
  fetchCollectionCounts,
} from "./lib/fetchRegistrationAnalytics";
import { REGISTRATION_COLLECTIONS } from "./lib/registrationsConfig";
import { loadAnalyticsConfig } from "./lib/schoolAliasesFirestore";
import {
  buildAliasLookup,
  buildHiddenKeySet,
  isHiddenSchoolResponse,
} from "./lib/schoolAliases";
import AnalyticsDashboard from "./components/admin/AnalyticsDashboard";
import RSVPAdminDashboard from "./components/admin/RSVPAdminDashboard";

export default function AdminPanel() {
  // ── Tab state ────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("analytics");

  // ── Analytics state ───────────────────────────────────────────────────────
  const [analyticsRecords, setAnalyticsRecords] = useState([]);
  const [collectionCounts, setCollectionCounts] = useState({});
  const [missingTimestamp, setMissingTimestamp] = useState(0);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState(null);

  // ── Analytics config (merges + hidden schools) ─────────────────────────
  const [aliasGroups, setAliasGroups] = useState([]);
  const [hiddenSchools, setHiddenSchools] = useState([]);

  const aliasLookup = React.useMemo(
    () => buildAliasLookup(aliasGroups),
    [aliasGroups]
  );
  const hiddenKeySet = React.useMemo(
    () => buildHiddenKeySet(hiddenSchools),
    [hiddenSchools]
  );

  const isResponseHidden = useCallback(
    (response) =>
      isHiddenSchoolResponse(response, hiddenKeySet, aliasLookup),
    [hiddenKeySet, aliasLookup]
  );

  // ── Competition responses state (lazy) ───────────────────────────────────
  const [responses, setResponses] = useState([]);
  const [filteredResponses, setFilteredResponses] = useState([]);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [responsesLoaded, setResponsesLoaded] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState("all");
  const [competitions, setCompetitions] = useState([]);

  const visibleResponses = React.useMemo(
    () => responses.filter((r) => !isResponseHidden(r)),
    [responses, isResponseHidden]
  );

  // ── Orders state (unchanged) ──────────────────────────────────────────────
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");

  // ── RSVP registrations state ──────────────────────────────────────────────
  const [rsvps, setRsvps] = useState([]);
  const [rsvpsLoading, setRsvpsLoading] = useState(false);
  const [rsvpsLoaded, setRsvpsLoaded] = useState(false);

  // ── Initial loads ─────────────────────────────────────────────────────────
  useEffect(() => {
    loadAnalytics();
    fetchOrders();
    fetchRSVPs();
    loadAnalyticsConfig().then(({ groups, hiddenSchools: hidden }) => {
      setAliasGroups(groups);
      setHiddenSchools(hidden);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When responses tab is first opened, trigger lazy load
  useEffect(() => {
    if (activeTab === "competitions" && !responsesLoaded) {
      fetchAllResponses();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterResponses();
  }, [selectedCompetition, visibleResponses]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterOrders();
  }, [orderStatusFilter, orders]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Analytics loader ──────────────────────────────────────────────────────
  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const [{ records, missingTimestamp: missing }, counts] =
        await Promise.all([fetchLeanAnalytics(), fetchCollectionCounts()]);
      setAnalyticsRecords(records);
      setCollectionCounts(counts);
      setMissingTimestamp(missing);
    } catch (err) {
      console.error("Analytics load error:", err);
      setAnalyticsError(err.message ?? "Unknown error");
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  // ── Full responses loader (lazy) ──────────────────────────────────────────
  const fetchAllResponses = async () => {
    setResponsesLoading(true);
    try {
      const competitionCollections = [
        ...REGISTRATION_COLLECTIONS,
      ];

      const allResponses = [];
      const competitionSet = new Set();

      for (const collectionName of competitionCollections) {
        try {
          let querySnapshot;
          if (collectionName === "ict_society_registrations") {
            try {
              querySnapshot = await getDocs(
                query(collection(db, collectionName), orderBy("timestamp", "desc"))
              );
            } catch {
              querySnapshot = await getDocs(collection(db, collectionName));
            }
          } else {
            try {
              querySnapshot = await getDocs(
                query(collection(db, collectionName), orderBy("submittedAt", "desc"))
              );
            } catch {
              try {
                querySnapshot = await getDocs(
                  query(collection(db, collectionName), orderBy("timestamp", "desc"))
                );
              } catch {
                querySnapshot = await getDocs(collection(db, collectionName));
              }
            }
          }

          querySnapshot.forEach((d) => {
            const data = d.data();
            let competitionTitle = data.competitionTitle;
            if (!competitionTitle) {
              if (collectionName === "ict_society_registrations") {
                competitionTitle = "Most Popular ICT Society";
              } else {
                competitionTitle = collectionName
                  .replace(/registrations_/g, "")
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase());
              }
            }
            competitionSet.add(competitionTitle);
            allResponses.push({
              id: d.id,
              collection: collectionName,
              competitionTitle,
              ...data,
              timestamp:
                data.timestamp?.toDate?.() ||
                data.submittedAt?.toDate?.() ||
                new Date(),
            });
          });
        } catch {
          // Collection doesn't exist yet — skip
        }
      }

      setResponses(allResponses);
      setCompetitions(Array.from(competitionSet).sort());
      setResponsesLoaded(true);
    } catch (error) {
      console.error("Error fetching responses:", error);
    } finally {
      setResponsesLoading(false);
    }
  };

  const filterResponses = () => {
    const base =
      selectedCompetition === "all"
        ? visibleResponses
        : visibleResponses.filter(
            (r) => r.competitionTitle === selectedCompetition
          );
    setFilteredResponses(base);
  };

  const handleAnalyticsConfigChange = ({ groups, hiddenSchools: hidden }) => {
    setAliasGroups(groups);
    setHiddenSchools(hidden);
  };

  // ── Orders ────────────────────────────────────────────────────────────────
  const fetchOrders = async () => {
    try {
      const ordersQuery = query(
        collection(db, "orders"),
        orderBy("timestamp", "desc")
      );
      const querySnapshot = await getDocs(ordersQuery);
      const ordersData = [];
      querySnapshot.forEach((d) => {
        ordersData.push({
          id: d.id,
          ...d.data(),
          timestamp: d.data().timestamp?.toDate?.() || new Date(),
        });
      });
      setOrders(ordersData);
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
  };

  const filterOrders = () => {
    if (orderStatusFilter === "all") {
      setFilteredOrders(orders);
    } else {
      setFilteredOrders(
        orders.filter((order) => (order.status || "pending") === orderStatusFilter)
      );
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const orderRef = doc(db, "orders", orderId);
      await updateDoc(orderRef, { status: newStatus });
      setOrders((prev) =>
        prev.map((order) =>
          order.id === orderId ? { ...order, status: newStatus } : order
        )
      );
      alert(`Order status updated to ${newStatus}`);
    } catch (error) {
      console.error("Error updating order status:", error);
      alert("Error updating order status");
    }
  };

  // ── RSVP Registrations ────────────────────────────────────────────────────
  const fetchRSVPs = async () => {
    setRsvpsLoading(true);
    try {
      let querySnapshot;
      try {
        querySnapshot = await getDocs(
          query(collection(db, "event-registrations"), orderBy("createdAt", "desc"))
        );
      } catch {
        querySnapshot = await getDocs(collection(db, "event-registrations"));
      }

      const rsvpData = [];
      querySnapshot.forEach((d) => {
        const data = d.data();
        rsvpData.push({
          id: d.id,
          ...data,
          createdAt:
            data.createdAt?.toDate?.() ||
            (data.createdAt ? new Date(data.createdAt) : new Date()),
        });
      });
      setRsvps(rsvpData);
      setRsvpsLoaded(true);
    } catch (error) {
      console.error("Error fetching RSVPs:", error);
    } finally {
      setRsvpsLoading(false);
    }
  };

  // ── CSV export ────────────────────────────────────────────────────────────
  const exportToCSV = () => {
    if (filteredResponses.length === 0) {
      alert("No data to export");
      return;
    }
    const allKeys = new Set();
    filteredResponses.forEach((response) => {
      Object.keys(response).forEach((key) => {
        if (key !== "schoolLogoBase64") allKeys.add(key);
      });
    });
    const headers = Array.from(allKeys);
    let csv = headers.join(",") + "\n";
    filteredResponses.forEach((response) => {
      const row = headers.map((header) => {
        let value = response[header];
        if (value === null || value === undefined) return "";
        if (typeof value === "object") {
          if (value instanceof Date) value = value.toISOString();
          else value = JSON.stringify(value);
        }
        value = String(value).replace(/"/g, '""');
        if (value.includes(",") || value.includes("\n") || value.includes('"')) {
          value = `"${value}"`;
        }
        return value;
      });
      csv += row.join(",") + "\n";
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    const fileName =
      selectedCompetition === "all"
        ? "all_competitions_responses.csv"
        : `${selectedCompetition.replace(/\s+/g, "_")}_responses.csv`;
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.reload();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === "object") {
      if (value instanceof Date) return value.toLocaleString();
      if (Array.isArray(value)) {
        return value
          .map((item, idx) =>
            typeof item === "object"
              ? `Member ${idx + 1}: ${JSON.stringify(item, null, 2)}`
              : item
          )
          .join("\n");
      }
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const formatFieldName = (key) =>
    key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();

  const FIELD_ORDER = [
    "competitionTitle", "competitionSchool", "participationType", "teamName",
    "schoolName", "participantName", "email", "clubEmail", "phone", "grade",
    "position", "contactPerson", "contactPhone", "hackerRankId", "teamMembers",
    "submissionLink", "additionalInfo", "timestamp", "submittedAt", "status",
  ];

  const getSortedEntries = (response) => {
    const sorted = [];
    FIELD_ORDER.forEach((key) => {
      if (
        Object.prototype.hasOwnProperty.call(response, key) &&
        key !== "id" && key !== "collection" && key !== "schoolLogoBase64"
      ) {
        sorted.push([key, response[key]]);
      }
    });
    Object.entries(response).forEach(([key, value]) => {
      if (
        !FIELD_ORDER.includes(key) &&
        key !== "id" && key !== "collection" && key !== "schoolLogoBase64"
      ) {
        sorted.push([key, value]);
      }
    });
    return sorted;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="admin-panel-container">
      <div className="admin-header">
        <h1>BTUI Admin Panel</h1>
        <div className="outreach-header-actions">
          <a href="/outreach" className="outreach-link-btn">
            Outreach
          </a>
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </div>

      <div className="tab-navigation">
        <button
          className={`tab-btn ${activeTab === "analytics" ? "active" : ""}`}
          onClick={() => setActiveTab("analytics")}
        >
          Analytics
        </button>
        <button
          className={`tab-btn ${activeTab === "competitions" ? "active" : ""}`}
          onClick={() => setActiveTab("competitions")}
        >
          Competition Responses{" "}
          {responsesLoaded ? `(${visibleResponses.length})` : ""}
        </button>
        <button
          className={`tab-btn ${activeTab === "orders" ? "active" : ""}`}
          onClick={() => setActiveTab("orders")}
        >
          Merchandise Orders ({orders.length})
        </button>
        <button
          className={`tab-btn ${activeTab === "rsvps" ? "active" : ""}`}
          onClick={() => setActiveTab("rsvps")}
        >
          Event RSVPs {rsvpsLoaded ? `(${rsvps.length})` : ""}
        </button>
      </div>

      {/* ── Analytics tab ─────────────────────────────────────────────────── */}
      {activeTab === "analytics" && (
        <AnalyticsDashboard
          records={analyticsRecords}
          missingTimestamp={missingTimestamp}
          loading={analyticsLoading}
          error={analyticsError}
          onRefresh={loadAnalytics}
          onAnalyticsConfigChange={handleAnalyticsConfigChange}
        />
      )}

      {/* ── Competitions tab ───────────────────────────────────────────────── */}
      {activeTab === "competitions" && (
        <>
          {hiddenSchools.length > 0 && (
            <div className="rename-status rename-status--hidden">
              {hiddenSchools.length} school{hiddenSchools.length !== 1 ? "s" : ""}{" "}
              hidden: {hiddenSchools.map((s) => s.label).join(", ")} — open
              Analytics → Control to manage
            </div>
          )}
          <div className="admin-controls">
            <div className="filter-section">
              <label>Filter by Competition:</label>
              <select
                value={selectedCompetition}
                onChange={(e) => setSelectedCompetition(e.target.value)}
              >
                <option value="all">
                  All Competitions ({visibleResponses.length})
                </option>
                {competitions.map((comp) => (
                  <option key={comp} value={comp}>
                    {comp} (
                    {
                      visibleResponses.filter((r) => r.competitionTitle === comp)
                        .length
                    }
                    )
                  </option>
                ))}
              </select>
            </div>
            <button onClick={exportToCSV} className="export-btn">
              Export to CSV ({filteredResponses.length} records)
            </button>
            <button onClick={fetchAllResponses} className="refresh-btn">
              Refresh Data
            </button>
          </div>

          {responsesLoading ? (
            <div className="loading">Loading responses…</div>
          ) : (
            <div className="responses-container">
              <h2>
                {selectedCompetition === "all"
                  ? "All Responses"
                  : selectedCompetition}{" "}
                ({filteredResponses.length})
              </h2>

              {filteredResponses.length === 0 ? (
                <p className="no-data">No responses found</p>
              ) : (
                <div className="responses-grid">
                  {filteredResponses.map((response, index) => (
                    <div key={response.id} className="response-card">
                      <div className="response-header">
                        <h3>Response #{index + 1}</h3>
                        <span className="competition-badge">
                          {response.competitionTitle}
                        </span>
                      </div>
                      <div className="response-details">
                        {response.schoolLogoBase64 && (
                          <div className="detail-row">
                            <strong>School Logo:</strong>
                            <div className="logo-preview">
                              <img
                                src={response.schoolLogoBase64}
                                alt="School Logo"
                                style={{ maxWidth: "100px", maxHeight: "100px" }}
                              />
                            </div>
                          </div>
                        )}
                        {getSortedEntries(response).map(([key, value]) => (
                          <div key={key} className="detail-row">
                            <strong>{formatFieldName(key)}:</strong>
                            <span className="detail-value">
                              {formatValue(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Orders tab ────────────────────────────────────────────────────── */}
      {activeTab === "orders" && (
        <>
          <div className="admin-controls">
            <div className="filter-section">
              <label>Filter by Status:</label>
              <select
                value={orderStatusFilter}
                onChange={(e) => setOrderStatusFilter(e.target.value)}
              >
                <option value="all">All Orders ({orders.length})</option>
                <option value="pending">
                  Pending ({orders.filter((o) => (o.status || "pending") === "pending").length})
                </option>
                <option value="confirmed">
                  Confirmed ({orders.filter((o) => o.status === "confirmed").length})
                </option>
                <option value="ready">
                  Ready for Pickup ({orders.filter((o) => o.status === "ready").length})
                </option>
                <option value="completed">
                  Completed ({orders.filter((o) => o.status === "completed").length})
                </option>
                <option value="cancelled">
                  Cancelled ({orders.filter((o) => o.status === "cancelled").length})
                </option>
              </select>
            </div>
            <button onClick={fetchOrders} className="refresh-btn">
              Refresh Orders
            </button>
          </div>

          <div className="orders-container">
            <h2>
              {orderStatusFilter === "all"
                ? "All Orders"
                : `${orderStatusFilter.charAt(0).toUpperCase() + orderStatusFilter.slice(1)} Orders`}{" "}
              ({filteredOrders.length})
            </h2>

            {filteredOrders.length === 0 ? (
              <p className="no-data">No orders found</p>
            ) : (
              <div className="orders-table-wrapper">
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Order Date</th>
                      <th>Customer</th>
                      <th>Contact</th>
                      <th>Admission #</th>
                      <th>Items</th>
                      <th>Total</th>
                      <th>Slip</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order, index) => (
                      <tr key={order.id}>
                        <td>{index + 1}</td>
                        <td className="date-cell">
                          {order.timestamp.toLocaleDateString()}
                          <br />
                          <small>{order.timestamp.toLocaleTimeString()}</small>
                        </td>
                        <td className="customer-cell">
                          <strong>{order.fullName}</strong>
                          <br />
                          <small>{order.email}</small>
                        </td>
                        <td>{order.phone}</td>
                        <td>{order.admissionNumber}</td>
                        <td className="items-cell">
                          {order.cart?.map((item, idx) => (
                            <div key={idx} className="item-row">
                              {item.name} x{item.quantity}
                              {item.size !== "N/A" && (
                                <span className="size-badge-small">
                                  ({item.size})
                                </span>
                              )}
                            </div>
                          ))}
                        </td>
                        <td className="amount-cell">
                          LKR {order.total?.toFixed(2)}
                        </td>
                        <td className="slip-cell">
                          {order.slipUrl ? (
                            <a
                              href={order.slipUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="slip-btn"
                            >
                              View
                            </a>
                          ) : (
                            <span className="no-slip">N/A</span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`status-badge-small status-${order.status || "pending"}`}
                          >
                            {(order.status || "pending").toUpperCase()}
                          </span>
                        </td>
                        <td className="actions-cell">
                          <select
                            value={order.status || "pending"}
                            onChange={(e) =>
                              updateOrderStatus(order.id, e.target.value)
                            }
                            className="status-select-compact"
                          >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="ready">Ready</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Event RSVPs tab ────────────────────────────────────────────────── */}
      {activeTab === "rsvps" && (
        <RSVPAdminDashboard
          rsvps={rsvps}
          loading={rsvpsLoading}
          onRefresh={fetchRSVPs}
        />
      )}
    </div>
  );
}
